import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import GoogleMapLocationPicker from "./GoogleMapLocationPicker";
import {
  Settings,
  Building2,
  Bell,
  Shield,
  Palette,
  Clock,
  Save,
  Upload,
  Trash2,
  AlertTriangle,
  Plus,
  X,
  Edit2,
  Dumbbell,
  Users,
  Power,
  Activity,
  Receipt,
  RefreshCw,
  Loader2,
  DollarSign,
} from "lucide-react";
import { API } from "@/App";
import { formatDateTR } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import NumberingSettings from "./NumberingSettings";
import { PublicBookingSettings } from "./PublicBookingSettings";

export default function SettingsModule({ businessId, business, onUpdate, user, isOwnBusiness, subscription, userPermissions }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "reports", "employees", "evaluations"]);
  const { currencyCode, setCurrencyCode, CURRENCIES, refreshCurrency, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType, getLocalizedMeasurement, getLocalizedExpenseType } = useLocalizedData();

  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  
  // Location change request state
  const [showLocationChangeModal, setShowLocationChangeModal] = useState(false);
  const [pendingLocationChange, setPendingLocationChange] = useState(null);
  const [submittingLocationChange, setSubmittingLocationChange] = useState(false);
  const [existingPendingRequest, setExistingPendingRequest] = useState(null);
  
  // Data migration state
  const [migratingPayments, setMigratingPayments] = useState(false);
  
  // Subscription check
  const hasActiveSubscription = subscription?.is_active && subscription?.plan === 'pro';

  // Settings page sub-permissions: owner sees all; for staff (trainer/employee with userPermissions),
  // each section is gated by its own flag (settings_business_info, settings_pricing, etc).
  const canSeeSection = (key) => {
    if (isOwnBusiness) return true;
    if (userPermissions?.is_owner) return true;
    return Boolean(userPermissions?.permissions?.[key]);
  };
  
  // Card prices state
  const [memberCards, setMemberCards] = useState([]);
  const [cardPrices, setCardPrices] = useState([]);
  const [savingCards, setSavingCards] = useState(false);

  // Working hours state
  const [workingHours, setWorkingHours] = useState({
    monday: { open: "09:00", close: "21:00", isOpen: true },
    tuesday: { open: "09:00", close: "21:00", isOpen: true },
    wednesday: { open: "09:00", close: "21:00", isOpen: true },
    thursday: { open: "09:00", close: "21:00", isOpen: true },
    friday: { open: "09:00", close: "21:00", isOpen: true },
    saturday: { open: "10:00", close: "18:00", isOpen: true },
    sunday: { open: "", close: "", isOpen: false },
  });
  const [savingHours, setSavingHours] = useState(false);

  // Branches and Training Types state
  const [availableBranches, setAvailableBranches] = useState([]);
  const [availableTrainingTypes, setAvailableTrainingTypes] = useState([]);
  const [businessBranches, setBusinessBranches] = useState([]);
  const [businessTrainingTypes, setBusinessTrainingTypes] = useState([]);
  const [branchDemoDurations, setBranchDemoDurations] = useState({}); // { "EMS": 30, "Pilates": 30, ... }
  const [newBranch, setNewBranch] = useState("");
  const [newBranchDemoDuration, setNewBranchDemoDuration] = useState(30); // Default 30 dk
  const [newTrainingType, setNewTrainingType] = useState("");
  const [savingBranches, setSavingBranches] = useState(false);
  const [savingTrainingTypes, setSavingTrainingTypes] = useState(false);

  // Measurement Parameters state
  const [globalMeasurementParams, setGlobalMeasurementParams] = useState([]);
  const [enabledMeasurementParams, setEnabledMeasurementParams] = useState([]);
  const [savingMeasurementParams, setSavingMeasurementParams] = useState(false);
  
  // Expense Types state
  const [allExpenseTypes, setAllExpenseTypes] = useState([]);
  const [enabledExpenseTypes, setEnabledExpenseTypes] = useState([]);
  const [expenseTypeConfigs, setExpenseTypeConfigs] = useState({});
  const [savingExpenseTypes, setSavingExpenseTypes] = useState(false);
  
  // Business Images state
  const [businessLogo, setBusinessLogo] = useState("");
  const [businessImages, setBusinessImages] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingImages, setSavingImages] = useState(false);
  
  // Salary and Rent Due Day state
  const [salaryDueDay, setSalaryDueDay] = useState(1);
  const [rentDueDay, setRentDueDay] = useState(1);
  const [savingDueDays, setSavingDueDays] = useState(false);
  
  // Business Status state
  const [businessIsActive, setBusinessIsActive] = useState(business?.is_active ?? true);
  const [savingBusinessStatus, setSavingBusinessStatus] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    // Business Info
    name: business?.name || "",
    phone: business?.phone || "",
    email: business?.email || "",
    address: business?.address || "",
    
    // Notifications
    reservationReminders: true,
    paymentReminders: true,
    
    // Reservation Settings
    minAdvanceBooking: 0.25, // 15 dakika
    maxAdvanceBooking: 30, // days
    cancellationWindow: 2, // hours - iptal süresi
    lateCancelPenalty: true, // geç iptal cezası uygula
    penaltyType: "burn_session", // burn_session veya fee
    penaltyFeeAmount: 0, // ceza ücreti (TL)
    autoConfirmReservations: false,
    allowReschedule: true, // yeniden planlama izni
    rescheduleWindow: 6, // hours - değiştirme süresi
    maxRescheduleCount: 2, // max değiştirme sayısı
    
    // Display
    showTrainersPublicly: true,
    allowOnlineBooking: true,
  });

  useEffect(() => {
    if (business) {
      // Load all business settings including visibility
      // display_settings comes merged from business_settings collection
      const displaySettings = business.display_settings || {};
      setSettings(prev => ({
        ...prev,
        name: business.name || "",
        phone: business.phone || "",
        email: business.email || "",
        address: business.address || "",
        showTrainersPublicly: displaySettings.show_trainers_publicly !== false, // default true
        allowOnlineBooking: displaySettings.allow_online_booking !== false, // default true
      }));
      // Load business branches and training types
      setBusinessBranches(business.branches || []);
      setBusinessTrainingTypes(business.training_types || []);
      // Load branch demo durations
      setBranchDemoDurations(business.branch_demo_durations || {});
      // Load salary and rent due days
      setSalaryDueDay(business.salary_due_day || 1);
      setRentDueDay(business.rent_due_day || 1);
      // Load business logo and images
      setBusinessLogo(business.logo || "");
      setBusinessImages(business.images || []);
      // Load business active status
      setBusinessIsActive(business.is_active ?? true);
      // Load working hours
      if (business.working_hours) {
        const hours = business.working_hours;
        setWorkingHours({
          monday: hours.monday ? { ...hours.monday, isOpen: true } : { open: "", close: "", isOpen: false },
          tuesday: hours.tuesday ? { ...hours.tuesday, isOpen: true } : { open: "", close: "", isOpen: false },
          wednesday: hours.wednesday ? { ...hours.wednesday, isOpen: true } : { open: "", close: "", isOpen: false },
          thursday: hours.thursday ? { ...hours.thursday, isOpen: true } : { open: "", close: "", isOpen: false },
          friday: hours.friday ? { ...hours.friday, isOpen: true } : { open: "", close: "", isOpen: false },
          saturday: hours.saturday ? { ...hours.saturday, isOpen: true } : { open: "", close: "", isOpen: false },
          sunday: hours.sunday ? { ...hours.sunday, isOpen: true } : { open: "", close: "", isOpen: false },
        });
      }
    }
    // Load reservation settings from backend
    if (businessId) {
      loadReservationSettings();
      loadBranchesAndTrainingTypes();
      loadMeasurementParams();
      loadExpenseTypes();
    }
  }, [business, businessId]);
  
  const loadBranchesAndTrainingTypes = async () => {
    try {
      const [branchesRes, typesRes] = await Promise.all([
        fetch(`${API}/public/branches`),
        fetch(`${API}/public/training-types`),
      ]);
      
      if (branchesRes.ok) {
        const data = await branchesRes.json();
        const branchList = data.branches || data || [];
        setAvailableBranches(branchList.map(b => typeof b === 'string' ? b : b.name));
      }
      
      if (typesRes.ok) {
        const data = await typesRes.json();
        const typeList = data.training_types || data || [];
        setAvailableTrainingTypes(typeList.map(t => typeof t === 'string' ? t : (t.name || t.id)));
      }
    } catch (error) {
      console.error("Error loading branches/types:", error);
    }
  };
  
  const loadMeasurementParams = async () => {
    try {
      // Fetch global measurement params
      const globalRes = await fetch(`${API}/public/measurement-params`);
      if (globalRes.ok) {
        const data = await globalRes.json();
        const params = data.params || [];
        setGlobalMeasurementParams(params.filter(p => p.is_active));
      }
      
      // Fetch business's enabled measurement params
      const businessRes = await fetch(`${API}/businesses/${businessId}/measurement-params`, {
        credentials: "include",
      });
      if (businessRes.ok) {
        const data = await businessRes.json();
        setEnabledMeasurementParams(data.enabled_params || []);
      }
    } catch (error) {
      console.error("Error loading measurement params:", error);
    }
  };
  
  const toggleMeasurementParam = (paramId) => {
    setEnabledMeasurementParams(prev => {
      if (prev.includes(paramId)) {
        return prev.filter(id => id !== paramId);
      } else {
        return [...prev, paramId];
      }
    });
  };
  
  const saveMeasurementParams = async () => {
    setSavingMeasurementParams(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/measurement-params`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ enabled_params: enabledMeasurementParams }),
      });
      
      if (response.ok) {
        toast.success(t('members:measurementParamsSaved'));
      } else {
        toast.error("Kaydedilemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingMeasurementParams(false);
    }
  };
  
  // Load expense types
  const loadExpenseTypes = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-types`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAllExpenseTypes(data.all_types || []);
        setEnabledExpenseTypes((data.enabled_types || []).map(t => t.id));
        setExpenseTypeConfigs(data.expense_type_configs || {});
      }
    } catch (error) {
      console.error("Error loading expense types:", error);
    }
  };
  
  const toggleExpenseType = (typeId) => {
    setEnabledExpenseTypes(prev => {
      if (prev.includes(typeId)) {
        // Remove from configs too
        setExpenseTypeConfigs(prevConfigs => {
          const newConfigs = { ...prevConfigs };
          delete newConfigs[typeId];
          return newConfigs;
        });
        return prev.filter(id => id !== typeId);
      } else {
        // Add with default config
        setExpenseTypeConfigs(prevConfigs => ({
          ...prevConfigs,
          [typeId]: { has_fixed_plan: false, amount: 0, due_day: 1 }
        }));
        return [...prev, typeId];
      }
    });
  };
  
  const toggleFixedPlan = (typeId, enabled) => {
    setExpenseTypeConfigs(prev => ({
      ...prev,
      [typeId]: {
        ...prev[typeId],
        has_fixed_plan: enabled,
        amount: enabled ? (prev[typeId]?.amount || 0) : 0,
        due_day: enabled ? (prev[typeId]?.due_day || 1) : 1
      }
    }));
  };
  
  const updateExpenseConfig = (typeId, field, value) => {
    setExpenseTypeConfigs(prev => ({
      ...prev,
      [typeId]: {
        ...prev[typeId],
        [field]: value
      }
    }));
  };
  
  const saveExpenseTypes = async () => {
    setSavingExpenseTypes(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/expense-types`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          enabled_expense_types: enabledExpenseTypes,
          expense_type_configs: expenseTypeConfigs
        }),
      });
      
      if (response.ok) {
        toast.success(t('payments:expenseTypesSaved'));
      } else {
        toast.error("Kaydedilemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingExpenseTypes(false);
    }
  };
  
  // Business Images Functions
  // Resize image before upload
  const resizeImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  
  // Logo change state (sadece değiştirme, silme yok)
  const [pendingLogo, setPendingLogo] = useState(null); // Yeni seçilen ama kaydedilmemiş logo
  const [savingLogo, setSavingLogo] = useState(false);
  
  // Logo değiştirme - dosya seçildiğinde çağrılır, hemen kaydetmez
  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error(t("common:toast_lutfenBirGorselDosyasiSecin"));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("common:toast_logoBoyutu5mbdanKucukOlmali"));
      return;
    }
    
    setUploadingLogo(true);
    try {
      // Resize logo
      const resizedFile = await resizeImage(file, 400, 400, 0.9);
      
      const formData = new FormData();
      formData.append("file", resizedFile);
      
      const response = await fetch(`${API}/upload/public-image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        const logoUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`;
        setPendingLogo(logoUrl); // Sadece pending state'e kaydet, backend'e değil
        toast.success(t("common:toast_logoYuklendiKaydetmekIcinButona"));
      } else {
        toast.error(t("common:toast_logoYuklenemedi"));
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(t("common:toast_logoYuklenirkenHataOlustu"));
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };
  
  // Logo değişikliğini iptal et
  const cancelLogoChange = () => {
    setPendingLogo(null);
  };
  
  // Logo değişikliğini kaydet
  const saveLogoChange = async () => {
    if (!pendingLogo) return;
    
    setSavingLogo(true);
    try {
      await saveBusinessLogo(pendingLogo);
      setBusinessLogo(pendingLogo);
      setPendingLogo(null);
      toast.success("Logo kaydedildi");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Logo kaydedilemedi");
    } finally {
      setSavingLogo(false);
    }
  };
  
  const saveBusinessLogo = async (logo) => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ logo }),
      });
      if (response.ok && onUpdate) onUpdate();
    } catch (error) {
      console.error("Error saving logo:", error);
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t("common:toast_lutfenBirGorselDosyasiSecin"));
      return;
    }
    
    // Validate file size (max 10MB before resize)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("common:toast_gorselBoyutu10mbdanKucukOlmali"));
      return;
    }
    
    setUploadingImage(true);
    try {
      // Resize image before upload
      const resizedFile = await resizeImage(file, 1200, 800, 0.85);
      
      const formData = new FormData();
      formData.append("file", resizedFile);
      
      // Upload the image
      const response = await fetch(`${API}/upload/public-image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        // Construct full URL
        const imageUrl = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`;
        
        // Add to images array
        const newImages = [...businessImages, imageUrl];
        setBusinessImages(newImages);
        
        // Save to backend
        await saveBusinessImages(newImages);
        toast.success(t("common:toast_gorselYuklendiVeOptimizeEdildi"));
      } else {
        toast.error(t("common:toast_gorselYuklenemedi"));
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(t('common:imageUploadError'));
    } finally {
      setUploadingImage(false);
      // Reset file input
      e.target.value = "";
    }
  };
  
  const removeImage = async (index) => {
    const newImages = businessImages.filter((_, i) => i !== index);
    setBusinessImages(newImages);
    await saveBusinessImages(newImages);
    toast.success(t('common:imageRemoved'));
  };
  
  const saveBusinessImages = async (images) => {
    setSavingImages(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ images }),
      });
      
      if (response.ok) {
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error("Error saving images:", error);
    } finally {
      setSavingImages(false);
    }
  };
  
  const loadReservationSettings = async () => {
    try {
      const response = await fetch(`${API}/business-settings/${businessId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const resSetting = data.reservation_settings || {};
        const displaySetting = data.display_settings || {};
        setSettings(prev => ({
          ...prev,
          minAdvanceBooking: resSetting.min_advance_booking || 2,
          minAdvanceComplete: resSetting.min_advance_complete || 0.5,
          maxAdvanceBooking: resSetting.max_advance_booking || 30,
          cancellationWindow: resSetting.cancellation_window || 24,
          lateCancelPenalty: resSetting.late_cancel_penalty ?? true,
          penaltyType: resSetting.penalty_type || "burn_session",
          penaltyFeeAmount: resSetting.penalty_fee_amount || 0,
          autoConfirmReservations: resSetting.auto_confirm || false,
          allowReschedule: resSetting.allow_reschedule ?? true,
          rescheduleWindow: resSetting.reschedule_window || 24,
          maxRescheduleCount: resSetting.max_reschedule_count || 2,
          // Display settings from business_settings collection
          showTrainersPublicly: displaySetting.show_trainers_publicly !== false,
          allowOnlineBooking: displaySetting.allow_online_booking !== false,
        }));
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };
  
  useEffect(() => {
    if (businessId) {
      fetchCardData();
      fetchPendingRequests();
    }
  }, [businessId, business?.accepted_cards]);
  
  // Fetch pending address change requests
  const fetchPendingRequests = async () => {
    try {
      const response = await authFetch(`${API}/businesses/${businessId}/pending-requests`);
      if (response.ok) {
        const requests = await response.json();
        // Find the most recent pending request
        const pending = requests.find(r => r.status === "pending");
        setExistingPendingRequest(pending || null);
      }
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    }
  };
  
  // Helper function to get auth token
  const getAuthToken = () => {
    const localToken = localStorage.getItem('auth_token');
    if (localToken) return localToken;
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') return value;
    }
    return null;
  };

  // Helper for authenticated fetch
  const authFetch = async (url, options = {}) => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...options, headers, credentials: 'include' });
  };

  const fetchCardData = async () => {
    try {
      console.log("Fetching card data...");
      const [cardsRes, pricesRes] = await Promise.all([
        fetch(`${API}/public/member-cards`),
        authFetch(`${API}/businesses/${businessId}/card-prices`),
      ]);
      
      console.log("Cards response status:", cardsRes.status);
      console.log("Prices response status:", pricesRes.status);
      
      // First load member cards
      let loadedCards = [];
      if (cardsRes.ok) {
        const data = await cardsRes.json();
        console.log("Member cards data:", data);
        loadedCards = data.member_cards || [];
        setMemberCards(loadedCards);
      } else {
        console.error("Failed to load member cards:", cardsRes.status);
      }
      
      if (pricesRes.ok) {
        const data = await pricesRes.json();
        // Handle both array and object formats for card_prices
        let existingPrices = data.card_prices;
        if (!Array.isArray(existingPrices)) {
          existingPrices = [];
        }
        
        // Initialize card prices from business accepted_cards (case-insensitive)
        const businessCards = (business?.accepted_cards || []).map(c => c.toLowerCase());
        
        if (existingPrices.length === 0 && businessCards.length > 0) {
          // No prices saved yet, initialize from business accepted_cards
          // Match with memberCards for proper casing
          const initialPrices = loadedCards
            .filter(card => businessCards.includes(card.toLowerCase()))
            .map(card => ({
              card_name: card,
              single_session_price: 0,
              is_active: true
            }));
          setCardPrices(initialPrices.length > 0 ? initialPrices : business.accepted_cards.map(card => ({
            card_name: card,
            single_session_price: 0,
            is_active: true
          })));
        } else if (existingPrices.length > 0) {
          // Prices exist, make sure business accepted_cards are reflected
          const updatedPrices = existingPrices.map(cp => ({
            ...cp,
            is_active: businessCards.includes(cp.card_name.toLowerCase())
          }));
          
          // Add any missing cards from business (case-insensitive)
          loadedCards.forEach(card => {
            if (businessCards.includes(card.toLowerCase()) && !updatedPrices.find(p => p.card_name.toLowerCase() === card.toLowerCase())) {
              updatedPrices.push({
                card_name: card,
                single_session_price: 0,
                is_active: true
              });
            }
          });
          
          setCardPrices(updatedPrices);
        } else if (businessCards.length > 0) {
          // No prices but has accepted_cards - initialize
          const initialPrices = loadedCards
            .filter(card => businessCards.includes(card.toLowerCase()))
            .map(card => ({
              card_name: card,
              single_session_price: 0,
              is_active: true
            }));
          setCardPrices(initialPrices);
        } else {
          setCardPrices([]);
        }
      }
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  };
  
  const getCardPrice = (cardName) => {
    const cp = cardPrices.find(c => c.card_name === cardName);
    return cp ? cp.single_session_price : 0;
  };
  
  // Get price for a specific training type, fallback to legacy single price
  const getCardTypePrice = (cardName, trainingType) => {
    const cp = cardPrices.find(c => c.card_name === cardName);
    if (!cp) return 0;
    const ppt = cp.prices_per_type || {};
    if (ppt[trainingType] !== undefined && ppt[trainingType] !== "") return ppt[trainingType];
    // Aliases
    const aliasMap = {
      "Özel Ders": ["one_on_one", "Birebir", "private"],
      "Düet": ["duet"],
      "Grup Dersi": ["Grup", "group"],
      "Online": ["online"],
    };
    const aliases = aliasMap[trainingType] || [];
    for (const a of aliases) {
      if (ppt[a] !== undefined && ppt[a] !== "") return ppt[a];
    }
    return cp.single_session_price || 0;
  };
  
  const isCardActive = (cardName) => {
    const cp = cardPrices.find(c => c.card_name === cardName);
    return cp ? cp.is_active : false;
  };
  
  const updateCardPrice = (cardName, price) => {
    setCardPrices(prev => {
      const existing = prev.find(c => c.card_name === cardName);
      if (existing) {
        return prev.map(c => c.card_name === cardName ? { ...c, single_session_price: parseInt(price) || 0 } : c);
      }
      return [...prev, { card_name: cardName, single_session_price: parseInt(price) || 0, is_active: true }];
    });
  };
  
  // Set price per training type (e.g., "Özel Ders": 500)
  const updateCardTypePrice = (cardName, trainingType, price) => {
    const parsed = price === "" ? 0 : (parseInt(price) || 0);
    setCardPrices(prev => {
      const existing = prev.find(c => c.card_name === cardName);
      if (existing) {
        return prev.map(c => {
          if (c.card_name !== cardName) return c;
          const ppt = { ...(c.prices_per_type || {}), [trainingType]: parsed };
          // Keep single_session_price in sync (use first non-zero) for legacy consumers
          const firstNonZero = Object.values(ppt).find(v => v && Number(v) > 0) || 0;
          return { ...c, prices_per_type: ppt, single_session_price: Number(firstNonZero) || 0 };
        });
      }
      return [...prev, {
        card_name: cardName,
        prices_per_type: { [trainingType]: parsed },
        single_session_price: parsed,
        is_active: true,
      }];
    });
  };
  
  const toggleCardActive = (cardName) => {
    setCardPrices(prev => {
      const existing = prev.find(c => c.card_name === cardName);
      if (existing) {
        return prev.map(c => c.card_name === cardName ? { ...c, is_active: !c.is_active } : c);
      }
      return [...prev, { card_name: cardName, single_session_price: 0, is_active: true }];
    });
  };
  
  const saveCardPrices = async () => {
    setSavingCards(true);
    try {
      // Save card prices
      const response = await fetch(`${API}/businesses/${businessId}/card-prices`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ card_prices: cardPrices }),
      });
      
      // Also update business accepted_cards list
      const activeCards = cardPrices.filter(c => c.is_active).map(c => c.card_name);
      await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accepted_cards: activeCards }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_kartAyarlariKaydedildi"));
        if (onUpdate) onUpdate();
      } else {
        toast.error(t('common:saveError'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingCards(false);
    }
  };

  // Working Hours Functions
  const updateWorkingHour = (day, field, value) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const toggleDayOpen = (day) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen }
    }));
  };

  const saveWorkingHours = async () => {
    setSavingHours(true);
    try {
      // Convert to backend format
      const hoursToSave = {};
      Object.entries(workingHours).forEach(([day, data]) => {
        if (data.isOpen && data.open && data.close) {
          hoursToSave[day] = { open: data.open, close: data.close };
        }
      });

      // Check if any trainer has hours outside the new business hours
      const trainersRes = await fetch(`${API}/businesses/${businessId}/trainers`, {
        credentials: "include",
      });
      
      if (trainersRes.ok) {
        const trainers = await trainersRes.json();
        const conflictingTrainers = [];
        
        for (const trainer of trainers.filter(t => t.status === "active")) {
          const scheduleRes = await fetch(`${API}/trainers/${trainer.trainer_id}/schedule?business_id=${businessId}`, {
            credentials: "include",
          });
          
          if (scheduleRes.ok) {
            const schedules = await scheduleRes.json();
            if (schedules.length > 0) {
              const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
              
              for (const schedule of schedules) {
                if (!schedule.is_active) continue;
                
                const dayName = dayNames[schedule.day_of_week];
                const businessDay = hoursToSave[dayName];
                
                if (businessDay) {
                  const timeToMins = (t) => {
                    const [h, m] = t.split(':').map(Number);
                    return h * 60 + m;
                  };
                  
                  const businessOpenMins = timeToMins(businessDay.open);
                  const businessCloseMins = timeToMins(businessDay.close);
                  const trainerStartMins = timeToMins(schedule.start_time);
                  const trainerEndMins = timeToMins(schedule.end_time);
                  
                  if (trainerStartMins < businessOpenMins || trainerEndMins > businessCloseMins) {
                    conflictingTrainers.push({
                      name: `${trainer.name} ${trainer.surname || ''}`.trim(),
                      day: dayName
                    });
                  }
                }
              }
            }
          }
        }
        
        if (conflictingTrainers.length > 0) {
          const trainerList = [...new Set(conflictingTrainers.map(t => t.name))].join(", ");
          toast.error(`Önce antrenörlerin çalışma saatlerini değiştirmelisiniz: ${trainerList}`, { duration: 6000 });
          setSavingHours(false);
          return;
        }
      }

      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ working_hours: hoursToSave }),
      });

      if (response.ok) {
        toast.success(t('settings:workingHoursSaved'));
        if (onUpdate) onUpdate();
      } else {
        toast.error(t('common:saveError'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingHours(false);
    }
  };

  // Branch Functions
  const addBranch = (branch) => {
    if (branch && !businessBranches.includes(branch)) {
      setBusinessBranches([...businessBranches, branch]);
      // Set default demo duration for new branch
      setBranchDemoDurations(prev => ({
        ...prev,
        [branch]: newBranchDemoDuration
      }));
    }
    setNewBranch("");
    setNewBranchDemoDuration(30); // Reset to default
  };

  const updateBranchDemoDuration = (branch, duration) => {
    setBranchDemoDurations(prev => ({
      ...prev,
      [branch]: duration
    }));
  };

  const removeBranch = async (branch) => {
    // Check if branch is in use by packages
    try {
      const packagesRes = await fetch(`${API}/packages?business_id=${businessId}`, {
        credentials: "include",
      });
      if (packagesRes.ok) {
        const packages = await packagesRes.json();
        const inUse = packages.some(p => p.branch === branch || p.training_type === branch);
        if (inUse) {
          toast.error(`"${branch}" aktif paketlerde kullanılıyor, silinemez`);
          return;
        }
      }
    } catch (error) {
      console.error("Error checking branch usage:", error);
    }
    
    // Check if branch is assigned to any trainer
    try {
      const trainersRes = await fetch(`${API}/trainers?business_id=${businessId}`, {
        credentials: "include",
      });
      if (trainersRes.ok) {
        const trainers = await trainersRes.json();
        const trainersUsingBranch = trainers.filter(t => 
          t.branches?.includes(branch) && t.status === "active"
        );
        if (trainersUsingBranch.length > 0) {
          const trainerNames = trainersUsingBranch.map(t => `${t.name} ${t.surname}`).join(", ");
          toast.error(`"${branch}" şu antrenörlere atanmış: ${trainerNames}. Önce antrenörlerden bu branşı kaldırın.`);
          return;
        }
      }
    } catch (error) {
      console.error("Error checking trainer branch usage:", error);
    }
    
    setBusinessBranches(businessBranches.filter(b => b !== branch));
    // Also remove demo duration for this branch
    setBranchDemoDurations(prev => {
      const updated = { ...prev };
      delete updated[branch];
      return updated;
    });
  };

  const saveBranches = async () => {
    setSavingBranches(true);
    try {
      // Eğer dropdown'da seçili branş varsa, onu da ekle
      let branchesToSave = [...businessBranches];
      let demoDurationsToSave = { ...branchDemoDurations };
      
      if (newBranch && !branchesToSave.includes(newBranch)) {
        branchesToSave.push(newBranch);
        demoDurationsToSave[newBranch] = newBranchDemoDuration;
        // State'i de güncelle
        setBusinessBranches(branchesToSave);
        setBranchDemoDurations(demoDurationsToSave);
        setNewBranch("");
        setNewBranchDemoDuration(30);
      }
      
      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          branches: branchesToSave,
          branch_demo_durations: demoDurationsToSave
        }),
      });

      if (response.ok) {
        toast.success(t("common:toast_branslarVeDemoSureleriKaydedildi"));
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || t("settings:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBranches(false);
    }
  };

  // Training Type Functions
  const addTrainingType = (type) => {
    if (type && !businessTrainingTypes.includes(type)) {
      setBusinessTrainingTypes([...businessTrainingTypes, type]);
    }
    setNewTrainingType("");
  };

  const removeTrainingType = async (type) => {
    // Check if training type is in use
    try {
      const packagesRes = await fetch(`${API}/packages?business_id=${businessId}`, {
        credentials: "include",
      });
      if (packagesRes.ok) {
        const packages = await packagesRes.json();
        const inUse = packages.some(p => p.training_type === type);
        if (inUse) {
          toast.error(`"${type}" aktif paketlerde kullanılıyor, silinemez`);
          return;
        }
      }
    } catch (error) {
      console.error("Error checking training type usage:", error);
    }
    setBusinessTrainingTypes(businessTrainingTypes.filter(t => t !== type));
  };

  const saveTrainingTypes = async () => {
    setSavingTrainingTypes(true);
    try {
      // Eğer dropdown'da seçili antrenman tipi varsa, onu da ekle
      let typesToSave = [...businessTrainingTypes];
      
      if (newTrainingType && !typesToSave.includes(newTrainingType)) {
        typesToSave.push(newTrainingType);
        // State'i de güncelle
        setBusinessTrainingTypes(typesToSave);
        setNewTrainingType("");
      }
      
      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ training_types: typesToSave }),
      });

      if (response.ok) {
        toast.success("Antrenman tipleri kaydedildi");
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || t("settings:saveError"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingTrainingTypes(false);
    }
  };

  // Migrate old package payments to payments collection
  const handleMigratePayments = async () => {
    setMigratingPayments(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/migrate-package-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.added > 0) {
          toast.success(`${result.added} ödeme kaydı düzeltildi!`);
        } else {
          toast.info(t("admin:toast_duzeltilecekOdemeKaydiBulunamadiTum"));
        }
        if (onUpdate) onUpdate();
      } else {
        toast.error(t("common:toast_migrationBasarisizOldu"));
      }
    } catch (error) {
      console.error("Migration error:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setMigratingPayments(false);
    }
  };

  // Save business info (name, phone, email) - directly saved
  // Address changes require admin approval
  const handleSaveBusinessInfo = async () => {
    setLoading(true);
    try {
      // Check if address changed
      const addressChanged = settings.address !== business?.address;
      
      // Save name, phone, email directly
      const response = await fetch(`${API}/businesses/${businessId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: settings.name,
          phone: settings.phone,
          email: settings.email,
          // Don't update address here if it changed - it needs approval
          ...(addressChanged ? {} : { address: settings.address })
        }),
      });
      
      if (response.ok) {
        if (addressChanged) {
          // Show info that name/phone/email saved but address needs approval
          toast.success(t('settings:businessInfoUpdated'));
          toast.info(t("settings:addressChangeHint"), { duration: 5000 });
        } else {
          toast.success(t('settings:businessInfoUpdated'));
        }
        if (onUpdate) onUpdate();
      } else {
        toast.error("Bilgiler kaydedilemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  // Request address/location change (requires admin approval)
  const handleRequestLocationChange = async () => {
    setSubmittingLocationChange(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/request-address-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          address: pendingLocationChange?.address || settings.address,
          latitude: pendingLocationChange?.latitude || business?.latitude,
          longitude: pendingLocationChange?.longitude || business?.longitude
        }),
      });
      
      if (response.ok) {
        toast.success(t("admin:toast_adresDegisikligiTalebiGonderildiAdmin"));
        setShowLocationChangeModal(false);
        setPendingLocationChange(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("settings:requestCouldNotBeSent"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmittingLocationChange(false);
    }
  };
  
  // Toggle business active status
  // Save business status (with button instead of instant toggle)
  const handleSaveBusinessStatus = async () => {
    setSavingBusinessStatus(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/toggle-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          is_active: businessIsActive,
          reason: businessIsActive ? t("settings:reactivated") : t("settings:businessDeactivated")
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("settings:statusCouldNotBeChanged"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBusinessStatus(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (deleteConfirm !== business?.name) {
      toast.error(t("common:toast_i̇sletmeAdiniDogruYazin"));
      return;
    }

    try {
      // In a real app, this would soft-delete the business
      toast.info(t("common:toast_i̇sletmeSilmeIslemiBackenddeHenuz"));
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-heading font-bold text-xl text-navy">{t('common:settings')}</h2>
        <p className="text-grey text-sm">{user?.role === "trainer" ? t("settings:managePersonalAndBusinessSettings") : t("settings:manageBusinessSettings")}</p>
      </div>

      {/* Business Information - Always accessible even without subscription */}
      {businessId && canSeeSection("settings_business_info") && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t("settings:businessInfo")}
              <span className="text-xs text-green-600 ml-2">✓ {t("settings:publiclyVisible")}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('settings:businessName')}</Label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                className="rounded-xl"
                data-testid="business-name-input"
              />
            </div>
            <div>
              <Label>{t('common:phone')}</Label>
              <Input
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="rounded-xl"
                data-testid="business-phone-input"
              />
            </div>
            <div>
              <Label>{t('common:email')}</Label>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="rounded-xl"
                data-testid="business-email-input"
              />
            </div>
            <div>
              <Label>{t("settings:addressReadOnly")}</Label>
              <Input
                value={settings.address}
                className="rounded-xl bg-slate-50"
                readOnly
                data-testid="business-address-input"
              />
              <p className="text-xs text-grey mt-1">{t("settings:addressChangeHint")}</p>
            </div>
          </div>
          
          {/* Location Change Button */}
          <div className="pt-4 border-t">
            {existingPendingRequest ? (
              // Show pending request status
              <div className="p-4 bg-amber-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <p className="font-medium text-amber-800">{t("common:adresDegisikligiTalebiBeklemede")}</p>
                </div>
                <div className="text-sm text-amber-700 space-y-1">
                  <p><strong>Talep Edilen Adres:</strong> {existingPendingRequest.proposed_data?.address}</p>
                  <p><strong>Koordinatlar:</strong> {existingPendingRequest.proposed_data?.latitude?.toFixed(4)}, {existingPendingRequest.proposed_data?.longitude?.toFixed(4)}</p>
                  <p><strong>Talep Tarihi:</strong> {formatDateTR(existingPendingRequest.created_at)}</p>
                </div>
                <p className="text-xs text-amber-600 mt-3">
                  ⏳ Admin onayı bekleniyor. Onay veya red sonucu size bildirilecektir.
                </p>
              </div>
            ) : (
              // Show location change button
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-navy">{t("settings:mapLocation")}</p>
                    <p className="text-sm text-grey">
                      {business?.latitude && business?.longitude 
                        ? `${business.latitude.toFixed(4)}, ${business.longitude.toFixed(4)}`
                        : t("settings:locationNotSet")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    onClick={() => {
                      setPendingLocationChange({
                        address: settings.address,
                        latitude: business?.latitude,
                        longitude: business?.longitude
                      });
                      setShowLocationChangeModal(true);
                    }}
                    data-testid="change-location-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    {t("settings:changeLocation")}
                  </Button>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ {t('settings:locationChangeRequiresAdmin')}
                </p>
              </>
            )}
          </div>
          
          <Button
            onClick={handleSaveBusinessInfo}
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl mt-4"
            data-testid="save-business-info-btn"
          >
            {loading ? t("settings:savingDots") : t("settings:saveBusinessInfo")}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Numbering Settings — member_no & package_no */}
      {businessId && canSeeSection("settings_numbering") && (
        <NumberingSettings businessId={businessId} />
      )}

      {businessId && canSeeSection("settings_numbering") && (
        <PublicBookingSettings businessId={businessId} />
      )}

      {/* Business Logo Management */}
      {businessId && canSeeSection("settings_business_info") && (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Upload className="w-4 h-4" />
            {t("settings:businessLogo")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Logo Görüntüleme */}
          <div>
            <p className="text-xs text-grey mb-3">
              {t("settings:logoHint")}
            </p>
            <div className="flex items-start gap-4">
              {/* Mevcut veya Yeni Logo Önizleme */}
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-100 border">
                {(pendingLogo || businessLogo) ? (
                  <img
                    src={pendingLogo || businessLogo}
                    alt={t("settings:businessLogo")}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <Upload className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="text-xs text-grey">
                  <p>• {t("settings:recommendedSize")}</p>
                  <p>• {t("settings:squarePreferred")}</p>
                  <p>• Max 5MB</p>
                </div>
                {/* Logo Değiştir Butonu */}
                <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg cursor-pointer transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                    disabled={uploadingLogo}
                  />
                  {uploadingLogo ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('common:loading')}</span>
                    </>
                  ) : (
                    <>
                      <Edit2 className="w-4 h-4" />
                      <span className="text-sm">{businessLogo ? t("settings:changeLogo") : t("settings:addLogo")}</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
          
          {/* Bekleyen Logo Değişikliği */}
          {pendingLogo && (
            <div className="p-3 bg-green-50 rounded-xl space-y-3">
              <p className="text-sm text-green-800">
                ✓ {t("settings:newLogoUploaded")}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelLogoChange}
                  className="flex-1"
                >
                  {t("settings:cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={saveLogoChange}
                  disabled={savingLogo}
                  className="flex-1 bg-navy hover:bg-navy/90"
                >
                  {savingLogo ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-3 h-3 mr-1" />
                      {t("settings:saveLogo")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
        </CardContent>
      </Card>
      )}

      {/* Business Status Toggle - Always accessible */}
      {businessId && canSeeSection("settings_business_info") && (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Power className="w-4 h-4" />
            {t("settings:businessStatus")}
            <span className="text-xs text-green-600 ml-2">✓ {t("settings:publiclyVisible")}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
            <div>
              <p className="font-medium">{t("settings:businessActive")}</p>
              <p className="text-sm text-grey">
                {businessIsActive 
                  ? t("settings:businessActiveDesc")
                  : t("settings:businessInactiveDesc")}
              </p>
            </div>
            <Switch
              checked={businessIsActive}
              onCheckedChange={setBusinessIsActive}
              data-testid="business-status-toggle"
            />
          </div>
          {!businessIsActive && (
            <div className="p-3 bg-amber-50 rounded-xl">
              <p className="text-sm text-amber-800">
                ⚠️ {t("settings:businessInactiveWarning")}
              </p>
            </div>
          )}
          <Button
            onClick={handleSaveBusinessStatus}
            disabled={savingBusinessStatus}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-business-status-btn"
          >
            {savingBusinessStatus ? t("settings:savingDots") : t("settings:saveBusinessStatus")}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Card Prices - Requires subscription */}
      {businessId && canSeeSection("settings_pricing") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Shield className="w-4 h-4" />
            {t("settings:cardPricesTitle")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 {t("settings:proRequired")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <p className="text-sm text-grey">
            {t("settings:cardPricesDesc")}
          </p>
          {memberCards.length > 0 ? (
            <div className="space-y-3">
              {memberCards.map((card) => (
                <div key={card} className="p-3 bg-slate-50 rounded-xl space-y-3" data-testid={`card-pricing-row-${card}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={isCardActive(card)}
                        onCheckedChange={() => toggleCardActive(card)}
                        data-testid={`card-active-toggle-${card}`}
                      />
                      <span className={`font-medium ${!isCardActive(card) ? "text-grey" : ""}`}>{card}</span>
                    </div>
                  </div>
                  {isCardActive(card) && businessTrainingTypes.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                      {businessTrainingTypes.map((tt) => (
                        <div key={tt} className="bg-white rounded-lg p-2 border border-slate-200">
                          <Label className="text-xs text-grey block mb-1">{getLocalizedTrainingType ? getLocalizedTrainingType(tt) : tt}</Label>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              value={getCardTypePrice(card, tt) || ""}
                              onChange={(e) => updateCardTypePrice(card, tt, e.target.value)}
                              className="rounded-lg h-8 text-sm"
                              placeholder="0"
                              data-testid={`card-price-${card}-${tt}`}
                            />
                            <span className="text-xs text-grey">{currencySymbol}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isCardActive(card) ? (
                    <p className="text-xs text-amber-600 pt-1">⚠️ {t("settings:noTrainingTypesWarning", "Önce 'Ders Tipleri' bölümünden işletmenizde sunulan ders tiplerini eklemelisiniz.")}</p>
                  ) : null}
                </div>
              ))}
              <Button
                onClick={saveCardPrices}
                disabled={savingCards}
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="save-card-prices-btn"
              >
                {savingCards ? t("settings:savingDots") : t("settings:saveCardPrices")}
              </Button>
            </div>
          ) : (
            <p className="text-center py-4 text-grey">{t("common:sistemdeTanimliKartYok")}</p>
          )}
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Hakediş Günleri - Maaş ve Kira */}
      {businessId && canSeeSection("settings_accruals") && (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t("settings:accrualDaysTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-grey">
            {t("settings:accrualDaysDesc")}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-xl space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">💵</span>
                <Label className="font-medium text-green-800">{t("employees:maasHakedisGunu")}</Label>
              </div>
              <p className="text-xs text-green-700">
                {t("settings:salaryExample")}
              </p>
              <Select value={salaryDueDay.toString()} onValueChange={(v) => setSalaryDueDay(parseInt(v))}>
                <SelectTrigger className="rounded-xl bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {t("settings:dayOfMonth", {day})}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-4 bg-orange-50 rounded-xl space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">🏠</span>
                <Label className="font-medium text-orange-800">{t("common:kiraTahsilatGunu")}</Label>
              </div>
              <p className="text-xs text-orange-700">
                {t("settings:rentExample")}
              </p>
              <Select value={rentDueDay.toString()} onValueChange={(v) => setRentDueDay(parseInt(v))}>
                <SelectTrigger className="rounded-xl bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {t("settings:dayOfMonth", {day})}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button
            onClick={async () => {
              setSavingDueDays(true);
              try {
                const response = await fetch(`${API}/businesses/${businessId}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    salary_due_day: salaryDueDay,
                    rent_due_day: rentDueDay,
                  }),
                });
                if (response.ok) {
                  toast.success(t("common:toast_hakedisGunleriKaydedildi"));
                  if (onUpdate) onUpdate();
                } else {
                  toast.error(t('settings:saveFailed'));
                }
              } catch (error) {
                toast.error(t('common:errorOccurred'));
              }
              setSavingDueDays(false);
            }}
            disabled={savingDueDays}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingDueDays ? t("settings:savingDots") : t("settings:saveAccrualDays")}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Working Hours - Requires subscription */}
      {businessId && canSeeSection("settings_working_hours") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t("settings:workingHoursTitle")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 {t("settings:proRequired")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <p className="text-sm text-grey">
            {t("settings:workingHoursDesc")}
          </p>
          <div className="space-y-3">
            {[
              { key: "monday", label: t('common:monday') },
              { key: "tuesday", label: t('common:tuesday') },
              { key: "wednesday", label: t('common:wednesday') },
              { key: "thursday", label: t('common:thursday') },
              { key: "friday", label: t('common:friday') },
              { key: "saturday", label: t('common:saturday') },
              { key: "sunday", label: t('common:sunday') },
            ].map(({ key, label }) => (
              <div key={key} className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={workingHours[key].isOpen}
                    onCheckedChange={() => toggleDayOpen(key)}
                    data-testid={`working-hours-toggle-${key}`}
                  />
                  <span className={`font-medium w-20 sm:w-24 text-sm ${!workingHours[key].isOpen ? "text-grey" : ""}`}>
                    {label}
                  </span>
                </div>
                {workingHours[key].isOpen ? (
                  <div className="flex items-center gap-2">
                    <Select
                      value={workingHours[key].open}
                      onValueChange={(v) => updateWorkingHour(key, "open", v)}
                    >
                      <SelectTrigger className="w-24 sm:w-28 rounded-lg h-8" data-testid={`working-hours-open-${key}`}>
                        <SelectValue placeholder={t("business:opening")} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {Array.from({ length: 48 }, (_, i) => {
                          const hour = Math.floor(i / 2);
                          const minute = (i % 2) * 30;
                          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                          return (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <span className="text-grey">-</span>
                    <Select
                      value={workingHours[key].close}
                      onValueChange={(v) => updateWorkingHour(key, "close", v)}
                    >
                      <SelectTrigger className="w-28 rounded-lg h-8" data-testid={`working-hours-close-${key}`}>
                        <SelectValue placeholder={t("business:closing")} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {Array.from({ length: 48 }, (_, i) => {
                          const hour = Math.floor(i / 2);
                          const minute = (i % 2) * 30;
                          const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                          return (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <span className="text-grey text-sm">{t("business:closed")}</span>
                )}
              </div>
            ))}
          </div>
          <Button
            onClick={saveWorkingHours}
            disabled={savingHours}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-working-hours-btn"
          >
            {savingHours ? t("settings:savingDots") : t("settings:saveWorkingHours")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Branches - Requires subscription */}
      {businessId && canSeeSection("settings_branches") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Dumbbell className="w-4 h-4" />
            {t("settings:branchesTitle")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 {t("settings:proRequired")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <p className="text-sm text-grey">
            {t("settings:branchesDesc")}
          </p>
          
          {/* Existing branches with demo durations */}
          <div className="space-y-2">
            {businessBranches.map((branch) => (
              <div
                key={branch}
                className="flex items-center justify-between bg-navy/5 px-3 py-2 rounded-xl"
              >
                <span className="font-medium text-navy">{getLocalizedBranch(branch)}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-grey">{t("settings:demoDuration")}</span>
                  <Select 
                    value={String(branchDemoDurations[branch] || 30)}
                    onValueChange={(val) => updateBranchDemoDuration(branch, parseInt(val))}
                  >
                    <SelectTrigger className="w-24 h-8 rounded-lg text-sm" data-testid={`demo-duration-${branch}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">{t("settings:min15")}</SelectItem>
                      <SelectItem value="30">{t("settings:min30")}</SelectItem>
                      <SelectItem value="60">{t("settings:hour1Select")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => removeBranch(branch)}
                    className="ml-1 hover:bg-navy/20 rounded-full p-1"
                    data-testid={`remove-branch-${branch}`}
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Add new branch */}
          <div className="flex gap-2 items-center border-t pt-3">
            <Select value={newBranch} onValueChange={setNewBranch}>
              <SelectTrigger className="flex-1 rounded-xl" data-testid="add-branch-select">
                <SelectValue placeholder={t("common:ph_bransSecin_6851")} />
              </SelectTrigger>
              <SelectContent>
                {availableBranches
                  .filter(b => !businessBranches.includes(b))
                  .map((branch) => (
                    <SelectItem key={branch} value={branch}>{getLocalizedBranch(branch)}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={String(newBranchDemoDuration)} onValueChange={(val) => setNewBranchDemoDuration(parseInt(val))}>
              <SelectTrigger className="w-24 rounded-xl" data-testid="new-branch-demo-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">{t("settings:min15")}</SelectItem>
                <SelectItem value="30">{t("settings:min30")}</SelectItem>
                <SelectItem value="60">{t("settings:hour1Select")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => addBranch(newBranch)}
              disabled={!newBranch}
              className="rounded-xl"
              data-testid="add-branch-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={saveBranches}
            disabled={savingBranches}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-branches-btn"
          >
            {savingBranches ? t("settings:savingDots") : t("settings:saveBranches")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Training Types - Requires subscription */}
      {businessId && canSeeSection("settings_training_types") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            {t("settings:trainingTypesTitle")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 {t("settings:proRequired")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <p className="text-sm text-grey">
            {t("settings:trainingTypesDesc")}
          </p>
          <div className="flex flex-wrap gap-2">
            {businessTrainingTypes.map((type) => (
              <div
                key={type}
                className="flex items-center gap-1 bg-teal/10 text-teal px-3 py-1.5 rounded-full text-sm"
              >
                <span>{getLocalizedTrainingType(type)}</span>
                <button
                  onClick={() => removeTrainingType(type)}
                  className="ml-1 hover:bg-teal/20 rounded-full p-0.5"
                  data-testid={`remove-training-type-${type}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Select value={newTrainingType} onValueChange={setNewTrainingType}>
              <SelectTrigger className="flex-1 rounded-xl" data-testid="add-training-type-select">
                <SelectValue placeholder={t("common:ph_antrenmanTipiSecin")} />
              </SelectTrigger>
              <SelectContent>
                {availableTrainingTypes
                  .filter(t => !businessTrainingTypes.includes(t))
                  .map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => addTrainingType(newTrainingType)}
              disabled={!newTrainingType}
              className="rounded-xl"
              data-testid="add-training-type-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={saveTrainingTypes}
            disabled={savingTrainingTypes}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-training-types-btn"
          >
            {savingTrainingTypes ? t("settings:savingDots") : t("settings:saveTrainingTypes")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Measurement Parameters - Requires subscription */}
      {businessId && canSeeSection("settings_measurements") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Activity className="w-4 h-4" />
            {t("settings:measurementParamsTitle")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 {t("settings:proRequired")}</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <p className="text-sm text-grey">
            {t("settings:measurementParamsDesc")}
          </p>
          {globalMeasurementParams.length > 0 ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {globalMeasurementParams.map((param) => (
                  <div
                    key={param.id}
                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${
                      enabledMeasurementParams.includes(param.id)
                        ? "bg-navy/10 border-navy/30"
                        : "bg-slate-50 border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => toggleMeasurementParam(param.id)}
                    data-testid={`measurement-param-${param.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                        enabledMeasurementParams.includes(param.id)
                          ? "bg-navy text-white"
                          : "bg-slate-200"
                      }`}>
                        {enabledMeasurementParams.includes(param.id) && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{getLocalizedMeasurement(param.name)}</p>
                        <p className="text-xs text-grey">{param.unit}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                onClick={saveMeasurementParams}
                disabled={savingMeasurementParams}
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="save-measurement-params-btn"
              >
                {savingMeasurementParams ? t("settings:savingDots") : t("settings:saveMeasurementParams")}
              </Button>
            </div>
          ) : (
            <div className="text-center py-4 text-grey">
              <p>{t("evaluations:sistemdeTanimliOlcumParametresiYok")}</p>
              <p className="text-xs mt-1">{t("admin:adminPanelindenOlcumParametreleriEklenme")}</p>
            </div>
          )}
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Expense Types */}
      {businessId && canSeeSection("settings_expense_types") && (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            {t("settings:expenseTypesTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-grey">
            {t("settings:expenseTypesDesc")}
            <span className="block mt-1 text-xs text-amber-600">{t("settings:otherAlwaysAvailable")}</span>
          </p>
          {allExpenseTypes.filter(t => t.id !== "diger").length > 0 ? (
            <div className="space-y-4">
              {allExpenseTypes.filter(t => t.id !== "diger").map((type) => (
                <div
                  key={type.id}
                  className={`p-4 rounded-xl border transition-colors ${
                    enabledExpenseTypes.includes(type.id)
                      ? "bg-red-50 border-red-200"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  {/* Header - Checkbox + Name */}
                  <div 
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => toggleExpenseType(type.id)}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                      enabledExpenseTypes.includes(type.id)
                        ? "bg-red-600 text-white"
                        : "bg-slate-200"
                    }`}>
                      {enabledExpenseTypes.includes(type.id) && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <p className="font-medium">{getLocalizedExpenseType(type.name)}</p>
                  </div>
                  
                  {/* Fixed Plan Settings - Only show when enabled */}
                  {enabledExpenseTypes.includes(type.id) && (
                    <div className="mt-4 pl-8 space-y-3">
                      {/* Toggle for Fixed Plan */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{t("admin:sabitOdemePlani")}</p>
                          <p className="text-xs text-grey">{t("common:herAyOtomatikBorcOlustur")}</p>
                        </div>
                        <Switch
                          checked={expenseTypeConfigs[type.id]?.has_fixed_plan || false}
                          onCheckedChange={(checked) => toggleFixedPlan(type.id, checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      {/* Amount and Due Day - Only show when Fixed Plan is ON */}
                      {expenseTypeConfigs[type.id]?.has_fixed_plan && (
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-red-200">
                          <div>
                            <Label className="text-xs">{t("settings:monthlyAmount")}</Label>
                            <Input
                              type="number"
                              placeholder="5000"
                              value={expenseTypeConfigs[type.id]?.amount || ""}
                              onChange={(e) => updateExpenseConfig(type.id, "amount", parseFloat(e.target.value) || 0)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded-lg h-9 mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">{t("settings:paymentDay")}</Label>
                            <Input
                              type="number"
                              min="1"
                              max="31"
                              placeholder="1"
                              value={expenseTypeConfigs[type.id]?.due_day || 1}
                              onChange={(e) => updateExpenseConfig(type.id, "due_day", Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded-lg h-9 mt-1"
                            />
                            <p className="text-xs text-grey mt-1">{t("settings:dayOfMonthShort", {day: 31})}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              <Button
                onClick={saveExpenseTypes}
                disabled={savingExpenseTypes}
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="save-expense-types-btn"
              >
                {savingExpenseTypes ? t("settings:savingDots") : t("settings:saveExpenseTypes")}
              </Button>
            </div>
          ) : (
            <div className="text-center py-4 text-grey">
              <p>{t("reports:sistemdeTanimliGiderTipiYok")}</p>
              <p className="text-xs mt-1">Admin panelinden gider tipleri eklenmelidir</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Notification Settings - Requires subscription */}
      {businessId && canSeeSection("settings_general") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Bildirim Ayarları
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 Pro gerekli</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t("common:rezervasyonHatirlatmalari")}</p>
              <p className="text-sm text-grey">{t("admin:uyelereDersOncesiHatirlatma")}</p>
            </div>
            <Switch
              checked={settings.reservationReminders}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, reservationReminders: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t("common:odemeHatirlatmalari")}</p>
              <p className="text-sm text-grey">{t("common:bekleyenOdemelerIcinHatirlatma")}</p>
            </div>
            <Switch
              checked={settings.paymentReminders}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, paymentReminders: checked })
              }
            />
          </div>
          <Separator />
          <Button
            onClick={async () => {
              setLoading(true);
              try {
                await fetch(`${API}/business-settings/${businessId}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    notification_settings: {
                      reservation_reminders: settings.reservationReminders,
                      payment_reminders: settings.paymentReminders,
                    }
                  }),
                });
                toast.success(t("common:toast_bildirimAyarlariKaydedildi"));
                if (onUpdate) onUpdate();
              } catch (error) {
                toast.error(t('settings:saveFailed'));
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-notification-settings-btn"
          >
            {loading ? t("settings:savingDots") : t("settings:saveNotificationSettings")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>

      )}

      {/* Reservation Settings - Requires subscription */}
      {businessId && canSeeSection("settings_general") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t("settings:reservationSettings")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 Pro gerekli</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t("settings:minAdvanceCreation")}</Label>
              <Select
                value={settings.minAdvanceBooking.toString()}
                onValueChange={(v) => setSettings({ ...settings, minAdvanceBooking: parseFloat(v) })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.25">{t("settings:minutes15")}</SelectItem>
                  <SelectItem value="0.5">{t("settings:minutes30")}</SelectItem>
                  <SelectItem value="1">{t("settings:hour1")}</SelectItem>
                  <SelectItem value="2">{t("settings:hours2")}</SelectItem>
                  <SelectItem value="4">{t("settings:hours4")}</SelectItem>
                  <SelectItem value="12">{t("settings:hours12")}</SelectItem>
                  <SelectItem value="24">{t("settings:hours24")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-grey mt-1">{t("common:uyeEnAzBuSure")}</p>
            </div>
            <div>
              <Label>{t("settings:minAdvanceCompletion")}</Label>
              <Select
                value={(settings.minAdvanceComplete || 0.5).toString()}
                onValueChange={(v) => setSettings({ ...settings, minAdvanceComplete: parseFloat(v) })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t("common:kisitlamaYok")}</SelectItem>
                  <SelectItem value="0.25">{t("settings:minutes15")}</SelectItem>
                  <SelectItem value="0.5">{t("settings:minutes30")}</SelectItem>
                  <SelectItem value="1">{t("settings:hour1")}</SelectItem>
                  <SelectItem value="2">{t("settings:hours2")}</SelectItem>
                  <SelectItem value="4">{t("settings:hours4")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-grey mt-1">{t("settings:completionAdvanceNote")}</p>
            </div>
            <div>
              <Label>{t("settings:maxAdvanceBookingDays")}</Label>
              <Select
                value={settings.maxAdvanceBooking.toString()}
                onValueChange={(v) => setSettings({ ...settings, maxAdvanceBooking: parseInt(v) })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t("settings:days7")}</SelectItem>
                  <SelectItem value="14">{t("settings:days14")}</SelectItem>
                  <SelectItem value="30">{t("settings:days30")}</SelectItem>
                  <SelectItem value="60">{t("settings:days60")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common:i̇ptalSuresi")}</Label>
              <Select
                value={settings.cancellationWindow.toString()}
                onValueChange={(v) => setSettings({ ...settings, cancellationWindow: parseFloat(v) })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.25">{t("settings:minutes15")}</SelectItem>
                  <SelectItem value="0.5">{t("settings:minutes30")}</SelectItem>
                  <SelectItem value="1">{t("settings:hour1")}</SelectItem>
                  <SelectItem value="2">{t("settings:hours2")}</SelectItem>
                  <SelectItem value="6">{t("settings:hours6")}</SelectItem>
                  <SelectItem value="12">{t("settings:hours12")}</SelectItem>
                  <SelectItem value="24">{t("settings:hours24")}</SelectItem>
                  <SelectItem value="48">{t("settings:hours48")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-grey mt-1">
                {settings.cancellationWindow >= 1 
                  ? `${settings.cancellationWindow} ${t("settings:hour1").split(" ")[1] || "hour"}` 
                  : `${settings.cancellationWindow * 60} ${t("settings:minutes15").split(" ")[1] || "min"}`} {t("settings:freeCancel")}
              </p>
            </div>
          </div>

          <Separator />
          
          {/* Late Cancellation Penalty */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t("common:gecİptalCezasi")}</p>
                <p className="text-sm text-grey">{t("common:sureGectiktenSonraIptalEden")}</p>
              </div>
              <Switch
                checked={settings.lateCancelPenalty}
                onCheckedChange={(checked) => 
                  setSettings({ ...settings, lateCancelPenalty: checked })
                }
              />
            </div>
            
            {settings.lateCancelPenalty && (
              <div className="pl-4 border-l-2 border-orange-200 space-y-3">
                <div>
                  <Label>{t("common:cezaTuru")}</Label>
                  <Select
                    value={settings.penaltyType}
                    onValueChange={(v) => setSettings({ ...settings, penaltyType: v })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="burn_session">{t("settings:burnSession")}</SelectItem>
                      <SelectItem value="fee">{t("common:sabitUcret")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {settings.penaltyType === "fee" && (
                  <div>
                    <Label>{t("settings:penaltyFee")}</Label>
                    <Input
                      type="number"
                      value={settings.penaltyFeeAmount}
                      onChange={(e) => setSettings({ ...settings, penaltyFeeAmount: parseInt(e.target.value) || 0 })}
                      className="rounded-xl"
                      placeholder="100"
                    />
                  </div>
                )}
                
                <p className="text-xs text-amber-600">
                  {settings.penaltyType === "burn_session" 
                    ? t("settings:lateCancelBurnNote")
                    : t("settings:lateCancelFeeNote", {amount: settings.penaltyFeeAmount})
                  }
                </p>
              </div>
            )}
          </div>

          <Separator />
          
          {/* Reschedule Settings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{t("common:rezervasyonDegistirme")}</p>
                <p className="text-sm text-grey">{t("common:uyelerRezervasyonlariniDegistirebilir")}</p>
              </div>
              <Switch
                checked={settings.allowReschedule}
                onCheckedChange={(checked) => 
                  setSettings({ ...settings, allowReschedule: checked })
                }
              />
            </div>
            
            {settings.allowReschedule && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-blue-200">
                <div>
                  <Label>{t("common:degistirmeSuresi")}</Label>
                  <Select
                    value={settings.rescheduleWindow.toString()}
                    onValueChange={(v) => setSettings({ ...settings, rescheduleWindow: parseFloat(v) })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.25">{t("settings:minutes15")}</SelectItem>
                      <SelectItem value="0.5">{t("settings:minutes30")}</SelectItem>
                      <SelectItem value="1">{t("settings:hour1")}</SelectItem>
                      <SelectItem value="2">{t("settings:hours2")}</SelectItem>
                      <SelectItem value="6">{t("settings:hours6")}</SelectItem>
                      <SelectItem value="12">{t("settings:hours12")}</SelectItem>
                      <SelectItem value="24">{t("settings:hours24")}</SelectItem>
                      <SelectItem value="48">{t("settings:hours48")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("common:maxDegistirmeSayisi")}</Label>
                  <Select
                    value={settings.maxRescheduleCount.toString()}
                    onValueChange={(v) => setSettings({ ...settings, maxRescheduleCount: parseInt(v) })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t("settings:times1")}</SelectItem>
                      <SelectItem value="2">{t("settings:times2")}</SelectItem>
                      <SelectItem value="3">{t("settings:times3")}</SelectItem>
                      <SelectItem value="5">5 kez</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <Separator />
          <Button
            onClick={async () => {
              setLoading(true);
              try {
                await fetch(`${API}/business-settings/${businessId}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    reservation_settings: {
                      min_advance_booking: settings.minAdvanceBooking,
                      min_advance_complete: settings.minAdvanceComplete,
                      max_advance_booking: settings.maxAdvanceBooking,
                      cancellation_window: settings.cancellationWindow,
                      late_cancel_penalty: settings.lateCancelPenalty,
                      penalty_type: settings.penaltyType,
                      penalty_fee_amount: settings.penaltyFeeAmount,
                      auto_confirm: false,
                      allow_reschedule: settings.allowReschedule,
                      reschedule_window: settings.rescheduleWindow,
                      max_reschedule_count: settings.maxRescheduleCount,
                    }
                  }),
                });
                toast.success(t("admin:toast_rezervasyonAyarlariKaydedildi"));
                if (onUpdate) onUpdate();
              } catch (error) {
                toast.error(t('settings:saveFailed'));
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-reservation-settings-btn"
          >
            {loading ? t("settings:savingDots") : t("settings:saveReservationSettings")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Display Settings - Requires subscription */}
      {businessId && canSeeSection("settings_general") && (
      <Card className={`border-0 shadow-sm ${!hasActiveSubscription ? "opacity-60" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Palette className="w-4 h-4" />
            {t("settings:appearanceSettings")}
            {!hasActiveSubscription && <span className="text-xs text-amber-600 ml-2">🔒 Pro gerekli</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasActiveSubscription ? (
            <div className="text-center py-4 text-grey">
              <p>{t("admin:premiumRequired")}</p>
            </div>
          ) : (
          <>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t("common:antrenorleriGoster")}</p>
              <p className="text-sm text-grey">{t("common:antrenorListesiProfildeGorunur")}</p>
            </div>
            <Switch
              checked={settings.showTrainersPublicly}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, showTrainersPublicly: checked })
              }
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t("settings:onlineReservation")}</p>
              <p className="text-sm text-grey">{t("common:uyelerOnlineRezervasyonYapabilir")}</p>
            </div>
            <Switch
              checked={settings.allowOnlineBooking}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, allowOnlineBooking: checked })
              }
            />
          </div>
          <Separator />
          <Button
            onClick={async () => {
              setLoading(true);
              try {
                await fetch(`${API}/business-settings/${businessId}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    display_settings: {
                      show_trainers_publicly: settings.showTrainersPublicly,
                      allow_online_booking: settings.allowOnlineBooking,
                    }
                  }),
                });
                toast.success(t("common:toast_gorunumAyarlariKaydedildi"));
                if (onUpdate) onUpdate();
              } catch (error) {
                toast.error(t('settings:saveFailed'));
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="save-display-settings-btn"
          >
            {loading ? t("settings:savingDots") : t("settings:saveAppearanceSettings")}
          </Button>
          </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Currency Settings */}
      {businessId && canSeeSection("settings_currency") && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-navy flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              {t("settings:currencySettings")}
            </CardTitle>
            <CardDescription>{t("settings:currencyDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select
                value={currencyCode}
                onValueChange={async (val) => {
                  try {
                    const token = localStorage.getItem("token");
                    const res = await fetch(`${API}/business-settings/${businessId}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const current = res.ok ? await res.json() : {};
                    await fetch(`${API}/business-settings/${businessId}`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ...current, currency: val }),
                    });
                    setCurrencyCode(val);
                    if (refreshCurrency) refreshCurrency();
                    toast.success(t("settings:currencySaved"));
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
              >
                <SelectTrigger className="w-48 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(CURRENCIES).map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Danger Zone */}
      {/* Danger Zone - Sadece İşletme Sahibi */}
      {businessId && isOwnBusiness && (
      <Card className="border-0 shadow-sm border-red-200 bg-red-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {t("settings:dangerZone")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600 mb-4">
            {t("settings:dangerZoneWarning")}
          </p>
          <Button
            variant="outline"
            className="rounded-xl text-red-600 border-red-300 hover:bg-red-100"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t("settings:deleteBusiness")}
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t("common:deleteBusiness")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-xl">
              <p className="text-sm text-red-700">
                <strong>{t("common:warning")}:</strong> {t("settings:deleteWarning")}
              </p>
            </div>

            <div>
              <Label>
                {t("settings:typeToConfirm", {name: business?.name})}
              </Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                className="rounded-xl mt-2"
                placeholder={t("common:businessName")}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowDeleteModal(false)}
              >
                {t("settings:cancel")}
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                onClick={handleDeleteBusiness}
                disabled={deleteConfirm !== business?.name}
              >
                {t("settings:permanentlyDelete")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Change Request Modal */}
      <Dialog open={showLocationChangeModal} onOpenChange={setShowLocationChangeModal}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-600" />
              {t("settings:locationChangeRequest")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-amber-50 rounded-xl">
              <p className="text-sm text-amber-800">
                {t("settings:locationChangeNote")}
              </p>
            </div>

            <div>
              <Label>{t("settings:currentAddress")}</Label>
              <Input
                value={business?.address || t("settings:notSpecified")}
                className="rounded-xl mt-1 bg-slate-50"
                disabled
              />
            </div>

            {/* Google Map Location Picker with Autocomplete */}
            <div className="space-y-2">
              <Label>{t("common:yeniKonumSecin")}</Label>
              <p className="text-xs text-grey mb-2">{t("common:adresYazarakArayinVeyaHaritada")}</p>
              <GoogleMapLocationPicker
                initialLocation={{
                  lat: business?.latitude || 41.0082,
                  lng: business?.longitude || 28.9784
                }}
                initialAddress={business?.address || ""}
                height="350px"
                onLocationSelect={(locationData) => {
                  // Use the most detailed address available
                  // Sometimes formatted_address is less detailed than what user searched
                  const bestAddress = locationData.formatted_address || locationData.searchText || "";
                  setPendingLocationChange(prev => ({
                    ...prev,
                    address: bestAddress,
                    latitude: locationData.lat,
                    longitude: locationData.lng,
                  }));
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common:latitude")}</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={pendingLocationChange?.latitude || ""}
                  onChange={(e) => setPendingLocationChange(prev => ({
                    ...prev,
                    latitude: parseFloat(e.target.value) || null
                  }))}
                  className="rounded-xl mt-1"
                  placeholder="41.0082"
                  data-testid="new-latitude-input"
                />
              </div>
              <div>
                <Label>{t("common:longitude")}</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={pendingLocationChange?.longitude || ""}
                  onChange={(e) => setPendingLocationChange(prev => ({
                    ...prev,
                    longitude: parseFloat(e.target.value) || null
                  }))}
                  className="rounded-xl mt-1"
                  placeholder="28.9784"
                  data-testid="new-longitude-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowLocationChangeModal(false);
                  setPendingLocationChange(null);
                }}
              >
                {t("settings:cancel")}
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-xl"
                onClick={handleRequestLocationChange}
                disabled={submittingLocationChange || !pendingLocationChange?.address}
                data-testid="submit-location-change-btn"
              >
                {submittingLocationChange ? t("settings:sending") : t("settings:sendRequest")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
